// src/utils/api.ts
import { supabase } from "../lib/supabaseClient";

/* ------------ Tipos ------------- */
export type PriceList = {
  id: string;
  name: string | null;
  slug: string;
  published: boolean;
  published_at: string | null;
  owner: string | null;
  created_at: string;
};

export type PriceItem = {
  id: number;
  codebar: string | null;
  product: string | null;
  lab: string | null;        // <- mapeamos desde "laboratorio"
  rubro: string | null;
  pvp: number | null;
  no_tax: number | null;     // <- mapeamos desde "sin_impuestos"
  created_at: string | null;
};

/* ------------ Listas ------------- */
export async function ensureList(slug: string, name: string): Promise<PriceList> {
  const { data: found, error: e1 } = await supabase
    .from("price_lists")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (e1) throw e1;
  if (found) return found as PriceList;

  const { data, error } = await supabase
    .from("price_lists")
    .insert({ slug, name }) // owner se setea por RLS (auth.uid())
    .select("*")
    .single();
  if (error) throw error;
  return data as PriceList;
}

export async function clearProducts(listId: string) {
  const { error } = await supabase.from("prices").delete().eq("list_id", listId);
  if (error) throw error;
}

/* ------------ Upload ------------- */
type RowInput = {
  codebar?: string | null;
  product?: string | null;
  lab?: string | null;             // vendrá como "lab" desde el Excel
  rubro?: string | null;
  pvp?: number | string | null;
  sin_impuestos?: number | string | null; // vendrá así desde el Excel
};

export async function uploadProductsChunked(
  listId: string,
  rows: RowInput[],
  chunk = 800,
  onProgress?: (inserted: number, total: number) => void
) {
  let inserted = 0;
  const total = rows.length;

  for (let i = 0; i < rows.length; i += chunk) {
    const slice = rows.slice(i, i + chunk).map((r) => ({
      list_id: listId,
      codebar: r.codebar ?? null,
      product: r.product ?? null,
      laboratorio: r.lab ?? null, // <- columna real en DB
      rubro: r.rubro ?? null,
      pvp: r.pvp === "" || r.pvp == null ? null : Number(r.pvp),
      sin_impuestos:
        r.sin_impuestos === "" || r.sin_impuestos == null
          ? null
          : Number(r.sin_impuestos),
    }));

    const { error } = await supabase.from("prices").insert(slice);
    if (error) throw error;

    inserted += slice.length;
    onProgress?.(inserted, total);
  }
}

/** Reemplaza toda la lista: borra todo y sube el nuevo Excel. */
export async function replaceProducts(
  listId: string,
  rows: RowInput[],
  chunk = 800,
  onProgress?: (inserted: number, total: number) => void
) {
  // 1) Borrar existentes (automático)
  await clearProducts(listId);

  // 2) Insertar nuevos
  await uploadProductsChunked(listId, rows, chunk, onProgress);
}

/* ------------ Publicar / Estado ------------- */
export async function setPublished(slug: string, value: boolean) {
  const { error } = await supabase
    .from("price_lists")
    .update({
      published: value,
      published_at: value ? new Date().toISOString() : null,
    })
    .eq("slug", slug);
  if (error) throw error;
}

export async function getPublishedListBySlug(slug: string): Promise<PriceList | null> {
  const { data, error } = await supabase
    .from("price_lists")
    .select("id,name,slug,published,published_at,owner,created_at")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return (data as PriceList) ?? null;
}

/* ------------ Buscador público ------------- */
type SearchOptions = {
  slug: string;
  q?: string;
  page?: number;      // 1-based
  pageSize?: number;  // default 50
};

export async function searchPrices(opts: SearchOptions): Promise<{
  items: PriceItem[];
  total: number;
}> {
  const { slug, q = "", page = 1, pageSize = 50 } = opts;

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from("prices")
    .select(
      `
        id,
        codebar,
        product,
        laboratorio,
        rubro,
        pvp,
        sin_impuestos,
        created_at,
        price_lists!inner ( slug, published )
      `,
      { count: "exact" }
    )
    .eq("price_lists.slug", slug)
    .eq("price_lists.published", true);

  if (q.trim()) {
    const term = q.trim().replace(/%/g, "");
    query = query.or(
      [
        `codebar.ilike.%${term}%`,
        `product.ilike.%${term}%`,
        `laboratorio.ilike.%${term}%`,
        `rubro.ilike.%${term}%`,
      ].join(",")
    );
  }

  query = query.order("product", { ascending: true }).range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;

  const items: PriceItem[] =
    (data ?? []).map((r: any) => ({
      id: r.id,
      codebar: r.codebar,
      product: r.product,
      lab: r.laboratorio,
      rubro: r.rubro,
      pvp: r.pvp,
      no_tax: r.sin_impuestos,
      created_at: r.created_at,
    })) ?? [];

  return { items, total: count ?? 0 };
}
